import React, { useState } from "react";
import rawWidgetScript from "./WidgetEmbedScript.html?raw";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";
import DeploymentSecurityGuide from "@/pages/Messaging/Deployments/DeploymentSecurityGuide";

interface Props {
  widgetId: string;
  siteId: number;
}

export default function WidgetEmbedCode({ widgetId, siteId }: Props) {
  const toastAlert = useToastAlert();
  const [copied, setCopied] = useState(false);

  const scriptSrc = `${import.meta.env.VITE_MESSAGING_CLIENT_URL ?? "https://messaging.velaro.com"}/widget.js`;

  // Strip HTML comments (CSP usage notes) — customers see clean embed code
  const embedCode = rawWidgetScript
    .replace(/<!--[\s\S]*?-->\n?/g, "")
    .replace("{{ siteId }}", String(siteId))
    .replace("{{ widgetId }}", widgetId)
    .replace("{{ scriptSrc }}", scriptSrc)
    .trim();

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      toastAlert.displayToast(Severity.Success, "Embed code copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toastAlert.displayToast(Severity.Error, "Copy failed — please select and copy manually.");
    }
  }

  return (
    <div className="flex flex-col gap-4 max-w-2xl">
      <div className="flex flex-col gap-1">
        <div className="font-semibold">Deploy this widget</div>
        <div className="text-sm text-gray-500">
          Paste this snippet into your page's HTML just before{" "}
          <code className="bg-gray-100 rounded px-1 text-xs">&lt;/body&gt;</code>, or add it
          via Google Tag Manager on the pages where this widget should appear.
        </div>
      </div>

      {/* Code block */}
      <div className="relative">
        <pre className="bg-gray-900 text-green-300 rounded-lg p-4 text-xs overflow-x-auto whitespace-pre-wrap font-mono leading-relaxed">
          {embedCode}
        </pre>
        <button
          onClick={handleCopy}
          className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded px-2 py-1 flex items-center gap-1 transition-colors"
          aria-label="Copy embed code"
        >
          <i className={`fa-solid ${copied ? "fa-check" : "fa-copy"}`} />
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>

      {/* Widget ID for reference */}
      <div className="flex items-center gap-2 text-xs text-gray-400">
        <span className="font-medium">Widget ID:</span>
        <code className="bg-gray-100 rounded px-2 py-0.5 font-mono">{widgetId}</code>
      </div>

      {/* GTM note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700 flex gap-3">
        <i className="fa-solid fa-circle-info mt-0.5 flex-shrink-0" />
        <div>
          <strong>Using Google Tag Manager?</strong> Add a new{" "}
          <em>Custom HTML</em> tag, paste the snippet above, and set the trigger
          to fire on the pages where this widget should appear. One GTM tag per
          page widget.
        </div>
      </div>

      {/* Display rules tip */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-700 flex gap-3">
        <i className="fa-solid fa-lightbulb mt-0.5 flex-shrink-0" />
        <div>
          <strong>Tip:</strong> You can also add this snippet site-wide and use{" "}
          <strong>Display Rules</strong> (in the previous tab) to control exactly which
          pages show this widget — no need to manage individual page placements.
        </div>
      </div>

      {/* Security guide */}
      <div className="flex flex-col gap-1 mt-2">
        <div className="font-semibold">Security: Content Security Policy &amp; more</div>
        <div className="text-sm text-gray-500 mb-2">
          If your website enforces a CSP, use the guides below. If not, the standard
          embed code works as-is.
        </div>
      </div>
      <DeploymentSecurityGuide embedScript={embedCode} variant="widget" />
    </div>
  );
}
